import { Cluster } from '@googlemaps/markerclusterer';
import { MarkerSizeAndAnchor, PopupOffset } from './cluster-renderer';
import { StandardClusterRenderer } from './standard-cluster.renderer';

export class TrafficCameraClusterRenderer extends StandardClusterRenderer {
  private customNormalSvgUrl = '/api/v1/meta/markers/traffic-camera-cluster-pin/40C057/0/0/normal/traffic-camera-cluster-pin.png';
  private customSelectedSvgUrl = '/api/v1/meta/markers/traffic-camera-cluster-pin/40C057/0/0/selected/traffic-camera-cluster-pin.png';
  private customMarkerSizeAndAnchor: MarkerSizeAndAnchor = {
    normal: { scaledSize: { width: 58, height: 58 }, anchor: { x: 29, y: 40 } },
    selected: { scaledSize: { width: 58, height: 58 }, anchor: { x: 29, y: 40 } }
  };
  popupOffset: PopupOffset = { y: 60 };
  includeMarkerDataForPopup = true;

  private checkForCameraCluster(cluster: Cluster): boolean {
    const position = cluster.markers[0]['__data'].position;
    // check if cameras are part of same cluster
    const isCameraCluster = cluster.markers.every(m => m['__data'].isCameraCluster && m['__data'].position === position);
    return isCameraCluster;
  }

  protected getClusterMarker(cluster: Cluster): google.maps.Marker {
    const isCameraCluster = this.checkForCameraCluster(cluster);

    if (isCameraCluster) {
      const { scaledSize, anchor } = this.customMarkerSizeAndAnchor.normal;

      const marker = new google.maps.Marker({
        visible: cluster.count > 0,
        position: cluster.position,
        icon: {
          url: this.customNormalSvgUrl,
          scaledSize: new google.maps.Size(scaledSize.width, scaledSize.height),
          anchor: new google.maps.Point(anchor.x, anchor.y),
          labelOrigin: new google.maps.Point(scaledSize.width / 2, scaledSize.height / 2 - 1)
        },
        label: {
          text: String(cluster.count),
          color: this.layerBorderColor,
          fontSize: '14px',
          fontWeight: '600'
        },
        // adjust zIndex to be above other markers
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count
      });

      marker['__data'] = {
        isCameraCluster: true,
        cameraClusterName: cluster?.markers[0]?.['__data']?.cameraName?.replace(/\s*\(.*?\)\s*/g, '')?.trim(),
        offsetBelow: 30
      };

      return marker;
    }

    return super.getClusterMarker(cluster);
  }

  public renderNormal(marker: google.maps.Marker): void {
    const { isSelected, isCameraCluster } = marker['__data'];

    if (isCameraCluster) {
      const { normal, selected } = this.customMarkerSizeAndAnchor;

      if (isSelected === true) {
        marker.setIcon({
          url: this.customSelectedSvgUrl,
          scaledSize: new google.maps.Size(selected.scaledSize.width, selected.scaledSize.height),
          anchor: new google.maps.Point(selected.anchor.x, selected.anchor.y),
          labelOrigin: new google.maps.Point(selected.scaledSize.width / 2, selected.scaledSize.height / 2 - 1)
        });
      } else {
        marker.setIcon({
          url: this.customNormalSvgUrl,
          scaledSize: new google.maps.Size(normal.scaledSize.width, normal.scaledSize.height),
          anchor: new google.maps.Point(normal.anchor.x, normal.anchor.y),
          labelOrigin: new google.maps.Point(normal.scaledSize.width / 2, normal.scaledSize.height / 2 - 1)
        });
      }
    } else {
      super.renderNormal(marker);
    }
  }

  public renderSelected(marker: google.maps.Marker) {
    const { isCameraCluster } = marker['__data'];

    if (isCameraCluster) {
      const { anchor, scaledSize } = this.customMarkerSizeAndAnchor.selected;

      marker.setIcon({
        url: this.customSelectedSvgUrl,
        scaledSize: new google.maps.Size(scaledSize.width, scaledSize.height),
        anchor: new google.maps.Point(anchor.x, anchor.y),
        labelOrigin: new google.maps.Point(scaledSize.width / 2, scaledSize.height / 2 - 1)
      });
    } else {
      super.renderSelected(marker);
    }
  }
}
